import { Skeleton, Space, Tabs } from "antd";
import React, { useCallback, useEffect, useMemo, useState, lazy } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchSeason } from "~entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import usePermissionsByCompetitionName from "../../hooks/usePermissionsByCompetitionName";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { useSportConfiguration } from "~hooks/useSportConfiguration";

const SeasonPools = lazy(() => import("../SeasonPools/SeasonPools"));
const EditSeason = lazy(() => import("../EditSeason/EditSeason"));
const SeasonStages = lazy(() => import("../SeasonStages/SeasonStages"));
const SeasonTeams = lazy(() => import("../SeasonTeams/SeasonTeams"));
const SeriesTab = lazy(() => import("../SeriesTab/SeriesTab"));
const SeasonSites = lazy(() => import("../Sites/SeasonSites"));
const SeasonVenues = lazy(() => import("../Venues/Season/SeasonVenues"));
const SeasonTeamImages = lazy(() => import("../SeasonTeamsImages/SeasonTeamImages"));
const Standings = lazy(() => import("~pages/Standings/StandingsPage"));
const FinalStandings = lazy(() => import("~pages/FinalStandings/FinalStandingsPage"));
const SeasonTeamStats = lazy(() => import("../SeasonTeamStats/SeasonTeamStats"));
const FixturesListPage = lazy(() => import("~pages/Fixtures/FixtureListPage"));
const SeasonReportsPage = lazy(() => import("~pages/Reports/SeasonReportsPage"));
const SeasonOfficialsPage = lazy(() => import('~pages/Officials/SeasonOfficialsPage'));
const AssignFixtureOfficialsPage = lazy(() => import('~pages/Officials/AssignFixtureOfficialsPage'));

const ManageSeasonWrapper = lazy(() => import("./ManageSeasonWrapper"));

const Loading = () => {
  return (
    <div className={"mt-4"}>
      <Space>
        <Skeleton.Input active={true} />
        <Skeleton.Input active={true} />
        <Skeleton.Input active={true} />
      </Space>
      <Skeleton title={true} active={true} />
    </div>
  );
};

const items = (data, seasonEntityPermissionName, fixturePermissionName) => [
  {
    key: "fixtures",
    label: <FormattedMessage id="fixtures" />,
    children: <FixturesListPage />,
    permissionKey: fixturePermissionName,
  },
  {
    key: "stages",
    label: <FormattedMessage id="stages" />,
    children: <SeasonStages />,
    permissionKey: PermissionNames.STAGE,
  },
  {
    key: "pools",
    label: <FormattedMessage id="pools" />,
    children: <SeasonPools />,
    permissionKey: PermissionNames.POOL,
  },
  {
    key: "series",
    label: <FormattedMessage id="series" />,
    children: <SeriesTab />,
    permissionKey: PermissionNames.SEASON_SERIES,
  },
  {
    key: "teams",
    label: <FormattedMessage id="teams" />,
    children: <SeasonTeams seasonData={data} />,
    permissionKey: seasonEntityPermissionName,
  },
  {
    key: "team-images",
    label: <FormattedMessage id="team.images" />,
    children: <SeasonTeamImages />,
    permissionKey: seasonEntityPermissionName,
  },
  {
    key: "entityStatistic",
    label: <FormattedMessage id="statistics.team" />,
    children: <SeasonTeamStats />,
    permissionKey: PermissionNames.SEASON_STATISTIC,
  },
  {
    key: "officials",
    label: <FormattedMessage id="statistics.seasonOfficials" />,
    children: <SeasonOfficialsPage />,
    permissionKey: PermissionNames.ROLES,
  },
  {
    key: "assign-officials",
    label: <FormattedMessage id="statistics.assignOfficials" />,
    children: <AssignFixtureOfficialsPage />,
    permissionKey: PermissionNames.ROLES,
  },
  {
    key: "sites",
    label: <FormattedMessage id="sites" />,
    children: <SeasonSites />,
    permissionKey: PermissionNames.SITE,
  },
  {
    key: "venues",
    label: <FormattedMessage id="venues" />,
    children: <SeasonVenues />,
    permissionKey: PermissionNames.VENUE,
  },
  {
    key: "standings",
    label: <FormattedMessage id="standings" />,
    children: <Standings />,
    permissionKey: PermissionNames.STANDING,
  },
  {
    key: "final",
    label: <FormattedMessage id="finalStandings" />,
    children: <FinalStandings />,
    permissionKey: PermissionNames.STANDING,
  },
  {
    key: "reports",
    label: <FormattedMessage id="reports" />,
    children: <SeasonReportsPage />,
    permissionKey: PermissionNames.REPORT,
  },
  {
    key: "edit",
    label: <FormattedMessage id="details" />,
    children: <EditSeason />,
    permissionKey: PermissionNames.SEASON,
  },
];

const SeasonPage = (props) => {
  const { sport, tab, customerId, organizationId, seasonId } = useParams();
  const { data, isLoading } = useFetchSeason();
  const { uiConfiguration } = useSportConfiguration();
  const [tabFromURL, setTabFromURL] = useState(null);
  const { permissions } = useUserPermissions();
  const getTabFromURL = useCallback((tabFromURL) => {
    setTabFromURL(tabFromURL);
  }, []);

  useEffect(() => {
    if (!isLoading && data.status) {
      if (data.status === 403) {
        navigate(`/${customerId}/admin/${sport}/${organizationId}/403`, {
          state: { back: `/${customerId}/admin/${sport}/${organizationId}/admin` },
        });
      }
    }
  }, [data, isLoading]);

  useEffect(() => {
    getTabFromURL(tab);
  }, [tab, getTabFromURL]);

  const { seasonEntityPermissionName, fixturePermissionName } = usePermissionsByCompetitionName();

  const navigate = useNavigate();

  const regularItems = useMemo(() => {
    return items(data?.data?.[0], seasonEntityPermissionName, fixturePermissionName);
  }, [data, seasonEntityPermissionName, fixturePermissionName]);

  const filteredItems = useMemo(() => {
    return regularItems.filter(
      (item) =>
        !(uiConfiguration.pages[item.key]?.enabled === false) &&
        (!item.permissionKey || permissions[item.permissionKey]?.includes(PermissionOperations.READ)),
    );
  }, [permissions, regularItems, uiConfiguration.pages]);

  useEffect(() => {
    if (regularItems.length > 0 && !regularItems.some((item) => item.key.toLowerCase() === tab.toLowerCase())) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/${regularItems[0].key}`);
    }
    if (filteredItems.length > 0 && !filteredItems.some((item) => item.key.toLowerCase() === tab.toLowerCase())) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/403`, {
        state: { back: `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}` },
      });
    }
  }, [filteredItems]);

  if (isLoading || !data) {
    return <Loading />;
  }

  return (
    <ManageSeasonWrapper data={data?.data?.[0]}>
      <div className={"tabs-wrap"}>
        <Tabs
          destroyInactiveTabPane={true}
          tabBarGutter={0}
          activeKey={tabFromURL}
          defaultActiveKey={"admin"}
          onChange={(key) => {
            navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/${key}`);
          }}
          items={filteredItems}
        />
      </div>
    </ManageSeasonWrapper>
  );
};

export default SeasonPage;
